import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { SignificationItem, TriadResponse } from '../../../../types/signification.types';
import { StepIndicator } from './StepIndicator';
import { NarrateStep } from './NarrateStep';
import {
  computeTriangleVertices,
  screenToBarycentric,
  barycentricToScreen,
  clampToSimplex,
  barycentricColorBlend,
  type Point2D,
  type BarycentricCoord,
  type TriangleVertices,
} from '../../../../utils/signification.math';
import { AUDIO } from '../../../../utils/audioFeedback';
import './TriadCapture.css';

interface TriadCaptureProps {
  item: SignificationItem;
  onComplete: (response: TriadResponse, audioBlob: Blob | null) => void;
  onClose: () => void;
  itemIndex: number;
  totalItems: number;
}

type Phase = 'place' | 'narrate';

const EDGE_SNAP_THRESHOLD = 0.92;
const CANVAS_PADDING = 75;
const POINT_RADIUS = 12;
const HALO_RADIUS = 28;
const FINGER_OFFSET_Y = -50; // Render circle 50px above touch point

/** Parse hex color to [r, g, b] tuple. */
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

export const TriadCapture: React.FC<TriadCaptureProps> = ({
  item,
  onComplete,
  onClose,
  itemIndex,
  totalItems,
}) => {
  const [phase, setPhase] = useState<Phase>('place');
  const [weights, setWeights] = useState<BarycentricCoord | null>([1/3, 1/3, 1/3]);
  const [revisionCount, setRevisionCount] = useState(0);
  const [activeVertexDesc, setActiveVertexDesc] = useState<number | null>(null);
  const [canvasReady, setCanvasReady] = useState(0);
  const startTimeRef = useRef(Date.now());
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  const fgCanvasRef = useRef<HTMLCanvasElement>(null);
  const verticesRef = useRef<TriangleVertices | null>(null);
  const sizeRef = useRef({ w: 0, h: 0 });
  const weightsRef = useRef<BarycentricCoord | null>(null);

  const hasNarrative = item.narrativeAnchor.enabled;

  // Pole colors
  // Neutral defaults — no red/green to avoid emotional bias
  const colorA = hexToRgb(item.poles[0]?.color || '#C2AC8E');
  const colorB = hexToRgb(item.poles[1]?.color || '#8B9EB7');
  const colorC = hexToRgb(item.poles[2]?.color || '#A8B4A0');

  // Reset on new item
  useEffect(() => {
    startTimeRef.current = Date.now();
  }, [item.id]);

  // Render background canvas (color field) — once on mount / resize
  const renderBackground = useCallback(() => {
    const canvas = bgCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = rect.width;
    const h = rect.height;
    sizeRef.current = { w, h };

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const vertices = computeTriangleVertices(w, h, CANVAS_PADDING);
    verticesRef.current = vertices;

    // Clear
    ctx.clearRect(0, 0, w, h);

    // Draw color field — sample at 4px intervals, clipped to triangle
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(vertices.A.x, vertices.A.y);
    ctx.lineTo(vertices.B.x, vertices.B.y);
    ctx.lineTo(vertices.C.x, vertices.C.y);
    ctx.closePath();
    ctx.clip();

    const step = 2;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        const bary = screenToBarycentric({ x, y }, vertices);
        if (bary.a < -0.01 || bary.b < -0.01 || bary.c < -0.01) continue;
        const clamped = clampToSimplex(bary);
        const color = barycentricColorBlend(clamped, colorA, colorB, colorC);
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.35;
        ctx.fillRect(x, y, step, step);
      }
    }
    ctx.restore();

    // Draw triangle outline — slightly thicker to mask sub-pixel edge artifacts
    ctx.beginPath();
    ctx.moveTo(vertices.A.x, vertices.A.y);
    ctx.lineTo(vertices.B.x, vertices.B.y);
    ctx.lineTo(vertices.C.x, vertices.C.y);
    ctx.closePath();
    ctx.strokeStyle = 'rgba(26, 24, 20, 0.15)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Setup foreground canvas
    const fgCanvas = fgCanvasRef.current;
    if (fgCanvas) {
      fgCanvas.width = w * dpr;
      fgCanvas.height = h * dpr;
      fgCanvas.style.width = `${w}px`;
      fgCanvas.style.height = `${h}px`;
    }
  }, [colorA, colorB, colorC]);

  // Render foreground (point + halo)
  const renderForeground = useCallback((bary: BarycentricCoord) => {
    const canvas = fgCanvasRef.current;
    const vertices = verticesRef.current;
    if (!canvas || !vertices) return;

    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, sizeRef.current.w, sizeRef.current.h);

    const screen = barycentricToScreen(bary, vertices);
    const color = barycentricColorBlend(bary, colorA, colorB, colorC);

    // Drop shadow
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
    ctx.shadowBlur = 12;
    ctx.shadowOffsetY = 4;

    // Halo
    ctx.beginPath();
    ctx.arc(screen.x, screen.y, HALO_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.3;
    ctx.fill();
    ctx.globalAlpha = 1;

    // White ring
    ctx.beginPath();
    ctx.arc(screen.x, screen.y, POINT_RADIUS + 3, 0, Math.PI * 2);
    ctx.fillStyle = 'white';
    ctx.fill();

    ctx.restore(); // Remove shadow for inner fill

    // Filled point
    ctx.beginPath();
    ctx.arc(screen.x, screen.y, POINT_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }, [colorA, colorB, colorC]);

  // Keep ref in sync so ResizeObserver can redraw after canvas clear
  useEffect(() => { weightsRef.current = weights; }, [weights]);

  useEffect(() => {
    renderBackground();
    setCanvasReady(c => c + 1);
    if (weightsRef.current) renderForeground(weightsRef.current);
    const observer = new ResizeObserver(() => {
      renderBackground();
      setCanvasReady(c => c + 1);
      if (weightsRef.current) renderForeground(weightsRef.current);
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [renderBackground, renderForeground]);

  // Clear foreground when weights are null
  useEffect(() => {
    if (weights) {
      renderForeground(weights);
    } else {
      const canvas = fgCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const dpr = window.devicePixelRatio || 1;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.clearRect(0, 0, sizeRef.current.w, sizeRef.current.h);
        }
      }
    }
  }, [weights, renderForeground]);

  const processPointer = useCallback((clientX: number, clientY: number) => {
    const container = containerRef.current;
    const vertices = verticesRef.current;
    if (!container || !vertices) return;

    const rect = container.getBoundingClientRect();
    // Apply vertical offset so the circle renders above the finger, not under it
    const point: Point2D = { x: clientX - rect.left, y: clientY - rect.top + FINGER_OFFSET_Y };
    const raw = screenToBarycentric(point, vertices);
    let bary = clampToSimplex(raw);

    // Edge snapping — snap to vertex when within threshold
    if (bary.a >= EDGE_SNAP_THRESHOLD) {
      bary = { a: 1, b: 0, c: 0 };
    } else if (bary.b >= EDGE_SNAP_THRESHOLD) {
      bary = { a: 0, b: 1, c: 0 };
    } else if (bary.c >= EDGE_SNAP_THRESHOLD) {
      bary = { a: 0, b: 0, c: 1 };
    }

    setWeights(bary);
    renderForeground(bary);
  }, [renderForeground]);

  const handlePointerDown = (e: React.PointerEvent) => {
    isDragging.current = true;
    setActiveVertexDesc(null);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    processPointer(e.clientX, e.clientY);
    if (!weights) AUDIO.tap();
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return;
    processPointer(e.clientX, e.clientY);
  };

  const handlePointerUp = () => {
    isDragging.current = false;
  };

  const handleConfirm = () => {
    if (hasNarrative) {
      setPhase('narrate');
    } else {
      finalize(null);
    }
  };

  const handleAdjust = () => {
    setRevisionCount(r => r + 1);
    setPhase('place');
  };

  const handleNarrate = (audioBlob: Blob | null) => {
    finalize(audioBlob);
  };

  const finalize = (audioBlob: Blob | null) => {
    if (!weights) return;
    const response: TriadResponse = {
      mode: 'triad',
      itemId: item.id,
      weights: [weights.a, weights.b, weights.c],
      placementDurationMs: Date.now() - startTimeRef.current,
      revisionCount,
    };
    onComplete(response, audioBlob);
  };

  const labels: [string, string, string] = [
    item.poles[0]?.label || 'A',
    item.poles[1]?.label || 'B',
    item.poles[2]?.label || 'C',
  ];

  // Vertex label positions — recomputed after canvas initialises / resizes
  const vertexPositions = useMemo(() => {
    if (!canvasReady) return null;
    const v = verticesRef.current;
    if (!v) return null;
    return {
      A: { x: v.A.x, y: v.A.y - 50 },    // Above top vertex, container extends down
      B: { x: v.B.x, y: v.B.y + 16 },    // Below-left vertex, centered
      C: { x: v.C.x, y: v.C.y + 16 },    // Below-right vertex, centered
    };
  }, [canvasReady]);

  // Narrate phase
  if (phase === 'narrate') {
    return (
      <NarrateStep
        prompt={item.narrativeAnchor.prompt}
        required={item.narrativeAnchor.required}
        onSubmit={handleNarrate}
        onBack={handleAdjust}
      />
    );
  }

  // Place phase
  return (
    <div className="trd-container">
      <StepIndicator current="place" hasNarrative={hasNarrative} />

      <div className="trd-header">
        {totalItems > 1 && (
          <div className="trd-counter">{itemIndex + 1} of {totalItems}</div>
        )}
        <div className="trd-prompt">{item.prompt}</div>
      </div>

      <div className="trd-canvas-area">
        <div
          ref={containerRef}
          className="trd-canvas-wrap"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{ touchAction: 'none' }}
        >
          <canvas ref={bgCanvasRef} className="trd-canvas trd-canvas--bg" />
          <canvas ref={fgCanvasRef} className="trd-canvas trd-canvas--fg" />

          {/* Dynamic color bloom overlays — intensity follows dot position */}
          {vertexPositions && weights && (
            <>
              <div
                className="trd-bloom trd-bloom--a"
                style={{
                  left: vertexPositions.A.x,
                  top: vertexPositions.A.y + 50,
                  background: `radial-gradient(circle at center, rgb(${colorA.join(',')}) 0%, transparent 70%)`,
                  opacity: weights.a * 0.55,
                }}
              />
              <div
                className="trd-bloom trd-bloom--b"
                style={{
                  left: vertexPositions.B.x,
                  top: vertexPositions.B.y - 16,
                  background: `radial-gradient(circle at center, rgb(${colorB.join(',')}) 0%, transparent 70%)`,
                  opacity: weights.b * 0.55,
                }}
              />
              <div
                className="trd-bloom trd-bloom--c"
                style={{
                  left: vertexPositions.C.x,
                  top: vertexPositions.C.y - 16,
                  background: `radial-gradient(circle at center, rgb(${colorC.join(',')}) 0%, transparent 70%)`,
                  opacity: weights.c * 0.55,
                }}
              />
            </>
          )}

          {/* Centroid invite — pulsing dot until user taps */}
          {!weights && (
            <div className="trd-centroid-invite" />
          )}

          {/* Vertex labels + descriptions */}
          {vertexPositions && (
            <>
              {[
                { pos: vertexPositions.A, color: colorA, label: labels[0], desc: item.poles[0]?.description, cls: 'trd-vertex--a', idx: 0 },
                { pos: vertexPositions.B, color: colorB, label: labels[1], desc: item.poles[1]?.description, cls: 'trd-vertex--b', idx: 1 },
                { pos: vertexPositions.C, color: colorC, label: labels[2], desc: item.poles[2]?.description, cls: 'trd-vertex--c', idx: 2 },
              ].map(v => (
                <div
                  key={v.idx}
                  className={`trd-vertex ${v.cls}`}
                  style={{ left: v.pos.x, top: v.pos.y, pointerEvents: 'auto', cursor: v.desc ? 'pointer' : 'default' }}
                  onClick={(e) => { e.stopPropagation(); if (v.desc) setActiveVertexDesc(activeVertexDesc === v.idx ? null : v.idx); }}
                >
                  <span className="trd-vertex-label" style={{ color: `rgb(${v.color.join(',')})` }}>
                    {v.label}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div className="trd-hint" style={{ visibility: weights ? 'hidden' : 'visible' }}>
        Tap inside the triangle to place yourself
      </div>

      {/* Description bar — shows tapped vertex description */}
      {activeVertexDesc !== null && item.poles[activeVertexDesc]?.description && (() => {
        const pole = item.poles[activeVertexDesc];
        const colors = [colorA, colorB, colorC];
        const c = colors[activeVertexDesc];
        return (
          <div className="trd-desc-bar" key={activeVertexDesc}>
            <span className="trd-desc-bar-label" style={{ color: `rgb(${c.join(',')})` }}>
              {pole.label}
            </span>
            <span className="trd-desc-bar-sep" style={{ background: `rgb(${c.join(',')})` }} />
            <span className="trd-desc-bar-text">{pole.description}</span>
          </div>
        );
      })()}

      <div className="trd-actions">
        <button
          className="trd-next-btn"
          disabled={!weights}
          onClick={() => { AUDIO.tap(); handleConfirm(); }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

